import { useEffect, useState } from "react";
import { Card, Form } from "react-bootstrap";
import { FaSearch } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import {
  getQuestionsList,
  qaSuggestion,
  sendAuditQuestions,
} from "../../api/common";
import Alert from "../../Components/AlertPopup";
import Breadcrumb from "../../Components/Breadcrumb";
import CustomDrilldown from "../../Components/DrilldownOffCanvas";
import LoadingOverlay from "../../Components/LoadingOverlay";
import MindmapFlow from "../../Components/TreeChart";
import { decrypt, getSessionStorage } from "../../utils";
import PageContainer from "../PageContainer";
import DrilldownQuestions from "./DrilldownQuestions";
import QuestionPreviewModal from "./Preview";
import styles from "./style.module.scss";
import Template from "./Template";

export interface QuestionData {
  questionText: string;
  artifacts: string;
  purpose?: string;
}

export interface QuestionSuggestionResponse {
  artifacts: string;
  purpose: string;
  question: string;
}
const sortTree = (arr: any[]): any[] =>
  [...arr].sort((a, b) => (a.id || "").localeCompare(b.id || "", undefined, { numeric: true })).map((item) =>
    item.children ? { ...item, children: sortTree(item.children) } : item
  );
const SelectQuestions = () => {
  const [userData, setuserData] = useState<any>([]);
  const encryptedUser = getSessionStorage("user");
  const [show, setShow] = useState(false);
  const [question, setQuestion] = useState("");
  const [customQuestion, setCustomQuestion] = useState<string[]>([]);
  const [isValidatedCustomQuestion, setIsValidatedCustomQuestion] =
    useState("");
  const [selectedClause, setSelectedClause] = useState<any>([]);
  const [AIQuestion, setAIQuestion] = useState<any>([]);
  const [selectedCheckBoxQuestion, setSelectedCheckBoxQuestion] = useState<any>(
    []
  );
  const [manualQuestion, setManualQuestions] = useState<any>([]);
  const [textShow, setTextShow] = useState(false);
  const [preview, setPreview] = useState(false);
  const [questionData, SetQuestionData] = useState<
    {
      complianceId: number;
      clauseId: number;
      questions: QuestionData[];
      topicName: string;
      policyName: string;
    }[]
  >([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredData, setFilteredData] = useState<
    QuestionSuggestionResponse[]
  >([]);
  const [btnDisable, setBtnDisable] = useState<boolean>(false);
  const [showToast, setShowToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [ErrorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitloading, setSubmitLoading] = useState(false);
  const [suggestionloading, setSuggestionLoading] = useState(false);
  const [rowData, setRowData] = useState<any[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<string>("Template 1");
  const [currentStep, setCurrentStep] = useState<number>(1);

  const { id, auditlevel, audittype } = useParams();
  const navigate = useNavigate();
  const handleClose = () => {
    setShowToast(false);
    setBtnDisable(false);
  };
  const handleErrorClose = () => setShowErrorToast(false);

  useEffect(() => {
    if (encryptedUser) {
      const decryptedData = decrypt(encryptedUser);
      const user = JSON.parse(decryptedData);
      setuserData(user);
    }
    setLoading(true);
    getQuestionsList(id!)
      .then((response) => {
        if (response) {
          const sortedData = sortTree(response);
          setFilteredData(sortedData);
          setRowData(sortedData);
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch(() => {
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sendQuestionnaires = () => {
    setSubmitLoading(true);
    setBtnDisable(true);
    const request = {
      questionsData: questionData,
      auditId: Number(decrypt(id!)),
    };

    sendAuditQuestions(request)
      .then((response: any) => {
        setShowToast(true);
        setSubmitLoading(false);
        setTimeout(function () {
          navigate("/in-progress");
        }, 3000);
      })
      .catch((err: any) => {
        setSubmitLoading(false);
        setBtnDisable(false);
        setErrorMessage("Error");
        setShowErrorToast(true);
      });
  };
  const renderButtonClass = () => {
    if (currentStep === 1) return styles.cardButton;
    else return `${styles.cardButton} mx-3`;
  };

  const handleAdd = (purpose?: string, artifacts?: string) => {
    const trimmedQuestion = question.trim();
    if (trimmedQuestion === "") return;
    const lowerQ = trimmedQuestion.toLowerCase();
    const existsInPrevious = questionData.some((group) =>
      group.questions?.some((q: any) => q.questionText?.toLowerCase() === lowerQ),
    );
    if (customQuestion.some((q) => q.toLowerCase() === lowerQ)) {
      setIsValidatedCustomQuestion("This question has already been added.");
      return;
    } else if (AIQuestion?.some((q: any) => q.question?.toLowerCase() === lowerQ)) {
      setIsValidatedCustomQuestion("This question already exists in AI suggestions.");
      return;
    } else if (existsInPrevious) {
      setIsValidatedCustomQuestion("This question has already been added.");
      return;
    }
    setCustomQuestion((prev) => [...prev, trimmedQuestion]);
    setSelectedCheckBoxQuestion((prev: any) => [
      ...prev,
      {
        questionText: trimmedQuestion,
        artifacts: artifacts?.trim() || "",
        isCustomQuestion: true,
        purpose: purpose?.trim() || "",
      },
    ]);
    setQuestion("");
    setTextShow(false);
  };

  const handleEdit = (complianceId: number, index: number, newText: string, newPurpose?: string, newArtifacts?: string) => {
    SetQuestionData((prev: any[]) =>
      prev.map((group) =>
        group.complianceId === complianceId
          ? {
            ...group,
            questions: group.questions.map((q: any, i: number) => {
              return i === index
                ? {
                    ...q,
                    questionText: newText,
                    ...(newPurpose !== undefined && { purpose: newPurpose }),
                    ...(newArtifacts !== undefined && { artifacts: newArtifacts }),
                  }
                : { ...q };
            }),
          }
          : group
      )
    );
  };

  const handleDelete = (complianceId: number, index: number) => {
    SetQuestionData((prev: any[]) =>
      prev
        .map((group) =>
          group.complianceId === complianceId
            ? {
              ...group,
              questions: group.questions.filter(
                (_: string, i: number) => i !== index
              ),
            }
            : group
        )
        .filter((group) => group.questions.length > 0)
    );
  };

  const handleLeafClick = (
    ids: any,
    label: string,
    complianceId: any,
    topicName: any,
    description: any,
    themeID: any,
    policyName: any
  ) => {
    setSuggestionLoading(true);
    setSelectedClause({
      clause: label,
      recordID: complianceId,
      clauseID: ids,
      topicName: topicName,
      description: description,
      themeID: themeID,
      policyName: policyName,
    });

    // Restore previously selected questions immediately (before API call)
    // so checkboxes appear checked while AI questions are loading
    const previouslySelected = questionData.find(
      (item) => item.complianceId === complianceId
    );

    if (previouslySelected) {
      setSelectedCheckBoxQuestion(previouslySelected.questions);
      const customQs = previouslySelected.questions
        .filter((q: any) => q.isCustomQuestion || !q.artifacts)
        .map((q: any) => q.questionText);
      setCustomQuestion(customQs);
    } else {
      setSelectedCheckBoxQuestion([]);
      setCustomQuestion([]);
    }

    const payload = {
      // "auditId": parseInt(decrypt(id!)),
      complianceId: complianceId,
      // "guidanceDocument": decrypt(audittype!),
      auditLevel: decrypt(auditlevel!),
    };
    qaSuggestion(payload)
      .then((response) => {
        setAIQuestion(response?.questions);
        setSuggestionLoading(false);
      })
      .catch((error) => setSuggestionLoading(false));
  };
  const handleCheckboxChange = (
    label: string,
    artifacts: string,
    checked: boolean,
    isCustomQuestion: boolean,
    purpose?: string
  ) => {
    if (checked) {
      const questionData = {
        questionText: label,
        artifacts,
        isCustomQuestion: isCustomQuestion,
        purpose: purpose || "",
      };
      // Add question if not already present
      setSelectedCheckBoxQuestion((prev: any) => [...prev, questionData]);
    } else {
      // Remove question if unchecked
      setSelectedCheckBoxQuestion((prev: any) =>
        prev.filter((q: any) => q.questionText !== label)
      );
    }
  };

  const handleDeleteQuestion = (label: string) => {
    setCustomQuestion((prev) => prev.filter((q) => q !== label));

    // Also remove from selectedCheckBoxQuestion
    setSelectedCheckBoxQuestion((prev: any) =>
      prev.filter((q: any) => q.questionText !== label)
    );
  };

  const handleUpdateQuestion = (oldLabel: string, newLabel: string, purpose?: string, artifacts?: string) => {
    if (!newLabel || !newLabel.trim()) return;

    setCustomQuestion((prev) =>
      prev.map((q) => (q === oldLabel ? newLabel : q))
    );

    // Also update in selectedCheckBoxQuestion
    setSelectedCheckBoxQuestion((prev: any) =>
      prev.map((q: any) =>
        q.questionText === oldLabel
          ? { ...q, questionText: newLabel, purpose: purpose ?? q.purpose, artifacts: artifacts ?? q.artifacts }
          : q
      )
    );
  };

  const handleSubmitQuestion = () => {
    const diff = selectedCheckBoxQuestion.filter(
      (item: any) =>
        !AIQuestion?.some((q: any) => q.question === item.questionText)
    );

    setManualQuestions((prev: any) => [...prev, ...diff]);
    setSelectedCheckBoxQuestion([]);
    setAIQuestion([]);
    setCustomQuestion([]);
    setTextShow(false);

    const newQuestions = [...selectedCheckBoxQuestion];

    if (newQuestions.length > 0) {
      SetQuestionData((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.complianceId === selectedClause?.recordID
        );

        if (existingIndex >= 0) {
          // Replace existing questions with current selection (handles both adds and removals)
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            questions: newQuestions,
          };
          return updated;
        } else {
          // Add new complianceId + clauseId with questions
          return [
            ...prev,
            {
              complianceId: selectedClause?.recordID,
              clauseId: selectedClause?.clauseID, // 👈 added
              topicName: selectedClause?.topicName,
              policyName: selectedClause?.policyName,
              description: selectedClause?.description,
              questions: newQuestions,
              auditThemesId: selectedClause?.themeID,
            },
          ];
        }
      });
    } else {
      // If no questions, remove that complianceId
      SetQuestionData((prev) =>
        prev.filter((item) => item.complianceId !== selectedClause?.recordID)
      );
    }

    setShow(false);
  };

  function searchPolicyData(data: any[], query: string) {
    if (!query.trim()) return data;

    const lowerQ = query.toLowerCase();

    // Recursive function to search through children
    const searchNode = (node: any): any | null => {
      const nameMatch = node.name?.toLowerCase().includes(lowerQ);
      const idMatch = node.id?.toLowerCase?.().includes(lowerQ);
      const typeMatch = node.type?.toLowerCase().includes(lowerQ);
      const complianceMatch = node.complianceId
        ? node.complianceId.toString().includes(lowerQ)
        : false;

      // Check children recursively
      const filteredChildren =
        node.children?.map(searchNode).filter(Boolean) || [];

      // If this node matches or any of its children match, keep it
      if (
        nameMatch ||
        idMatch ||
        typeMatch ||
        complianceMatch ||
        filteredChildren.length > 0
      ) {
        return {
          ...node,
          children:
            filteredChildren.length > 0 ? filteredChildren : node.children,
        };
      }

      return null;
    };

    return data.map(searchNode).filter(Boolean);
  }

  const executeSearch = () => {
    const result = searchPolicyData(rowData, searchQuery);
    setFilteredData(result); // return filtered data to parent
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      executeSearch();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (value.trim() === "") {
      setFilteredData(rowData);
    }
  };

  const breadcrumbItems = [
    { label: "Audit Scheduled", path: "/audit-scheduled" },
    { label: `Assessment Questionnaire (${decrypt(audittype!)})` },
  ];

  return (
    <>
      <PageContainer>
        <LoadingOverlay loading={loading} />

        <div className={styles.title}>
          <Alert
            type="error"
            show={showErrorToast}
            onClose={handleErrorClose}
            message={ErrorMessage}
          />

          <Alert
            show={showToast}
            onClose={handleClose}
            message={"Questionnaires sent to the auditees successfully!!"}
          />
          <div
            className={`modalTitle d-flex ${currentStep === 1 ? "text-start" : "text-center"}`}
          >
            <Breadcrumb items={breadcrumbItems} />
            {currentStep === 1 && (
              <div className={styles.SearchWrap}>
                <div className={`${styles.searchContainer}`}>
                  <Form.Control
                    type="text"
                    placeholder="Search"
                    className={styles.searchInput}
                    value={searchQuery}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                  />
                  <FaSearch
                    className={styles.searchIcon}
                    onClick={executeSearch}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <br />
        <Card.Body
          className={
            currentStep === 1 ? styles.cardBody : styles.cardBodyTemplate
          }
        >
          {(() => {
            switch (currentStep) {
              case 1:
                return (
                  <div className="p-3">
                    {loading ? (
                      <div
                        style={{
                          height: "250px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {/* <PulseLoader size={20} color="#4a4a4a" /> */}
                      </div>
                    ) : (
                      <>
                        {filteredData.length > 0 && (
                          <MindmapFlow
                            questionData={filteredData}
                            selectQuestions={questionData}
                            setPreview={setPreview}
                            renderButtonClass={renderButtonClass}
                            onLeafClick={(
                              id: any,
                              label: any,
                              recordID: any,
                              clauseID: any,
                              topicName: any,
                              description: any,
                              themeID: any,
                              policyName: any
                            ) => {
                              handleLeafClick(
                                clauseID,
                                label,
                                recordID,
                                topicName,
                                description,
                                Number(themeID),
                                policyName
                              );
                              setShow(true);
                            }}
                          />
                        )}
                      </>
                    )}
                    <CustomDrilldown
                      show={show}
                      title={
                        <div>
                          <strong>Clause ID:</strong>&nbsp;&nbsp;
                          {selectedClause?.clauseID}
                          <br />
                          <br />
                          <strong>Description:</strong>&nbsp;&nbsp;
                          {selectedClause?.description ?? selectedClause?.clause ?? "-"}
                        </div>
                      }
                      onClose={() => {
                        setShow(false);
                        setCustomQuestion([]);
                        setTextShow(false);
                        setSelectedCheckBoxQuestion([]);
                        setAIQuestion([]);
                      }}
                      width="70"
                    >
                      <DrilldownQuestions
                        AIQuestion={AIQuestion}
                        customQuestion={customQuestion}
                        textShow={textShow}
                        handleAdd={handleAdd}
                        question={question}
                        setTextShow={(value) => setTextShow(value)}
                        isValidatedCustomQuestion={isValidatedCustomQuestion}
                        setIsValidatedCustomQuestion={(value) =>
                          setIsValidatedCustomQuestion(value)
                        }
                        setQuestion={setQuestion}
                        handleCheckboxChange={handleCheckboxChange}
                        handleSubmitQuestion={handleSubmitQuestion}
                        renderButtonClass={renderButtonClass}
                        handleLeafClick={handleLeafClick}
                        selectedClause={selectedClause}
                        selectedCheckBoxQuestion={selectedCheckBoxQuestion}
                        handleUpdateQuestion={handleUpdateQuestion}
                        handleDeleteQuestion={handleDeleteQuestion}
                        questionData={questionData}
                        suggestionloading={suggestionloading}
                      />
                    </CustomDrilldown>
                    <QuestionPreviewModal
                      show={preview}
                      data={questionData}
                      manualQuestions={manualQuestion}
                      onClose={() => setPreview(false)}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onProceed={() => {
                        setCurrentStep(2);
                        setPreview(false);
                      }}
                    />
                  </div>
                );
              case 2:
                return (
                  <Template
                    selectedTemplate={selectedTemplate}
                    currentStep={currentStep}
                    btnDisable={btnDisable}
                    QuestionData={questionData}
                    setCurrentStep={setCurrentStep}
                    setSelectedTemplate={setSelectedTemplate}
                    sendQuestionnaires={sendQuestionnaires}
                    renderButtonClass={renderButtonClass}
                    loading={submitloading}
                    showToast={showToast}
                  />
                );
              default:
                return null;
            }
          })()}
        </Card.Body>
        <br />
      </PageContainer>
    </>
  );
};

export default SelectQuestions;
